import { ApolloClient } from '@apollo/client';
import type {
  CategoryUpdateTabsInput,
  CategoryUpdateTagsInput,
  CategoryCreationTagsInput,
  TabCreationWithoutCategoryInput,
  CategoryUpdateTabsUpdateInput,
} from '../../../__generated__/queries-web';
import { TabType } from '../../../__generated__/queries-web';
import type { UseSavePageCategoryVariables } from './mutations/category.web.graphql';
import { PAGE_CATEGORY_FRAGMENT } from './fragments/category.web.graphql';
import type {
  CategoryFormSectionBoardQualifier,
  CategoryFormSectionBoardTag,
  SectionTabQualifier,
  SectionTabTag,
} from '../views/Edit/components/Section/types';

const normalizeTabTags = (
  tags?: CategoryFormSectionBoardTag[] | SectionTabTag[],
): CategoryFormSectionBoardTag[] => {
  if (!tags?.length) {
    return [];
  }

  if ('tag' in tags[0]) {
    return (tags as SectionTabTag[]).map(({ tag }) => ({
      id: tag.id,
      title: tag.title,
    }));
  }

  return tags as CategoryFormSectionBoardTag[];
};

const normalizeTabQualifiers = (
  qualifiers?: CategoryFormSectionBoardQualifier[] | SectionTabQualifier[],
): CategoryFormSectionBoardQualifier[] => {
  if (!qualifiers?.length) {
    return [];
  }

  if ('qualifier' in qualifiers[0]) {
    return (qualifiers as SectionTabQualifier[]).map(({ qualifier }) => ({
      id: qualifier.id,
      title: qualifier.title,
    }));
  }

  return qualifiers as CategoryFormSectionBoardQualifier[];
};

type TabTagsCache = { tag: { id: string } }[];
type TabQualifiersCache = { qualifier: { id: string } }[];

interface TabTagsMutationInput {
  create?: { order: number; tag: { connect: { id: string } } }[];
  delete?: { tag: { id: string } }[];
}

interface TabQualifiersMutationInput {
  create?: { order: number; qualifier: { connect: { id: string } } }[];
  delete?: { qualifier: { id: string } }[];
}

const getTabTagsMutationInput = (
  tags: CategoryFormSectionBoardTag[],
  cachedTags: TabTagsCache | undefined,
  isCreate: boolean,
): TabTagsMutationInput => {
  const createInput = tags.map((tag, index) => ({
    order: index + 1,
    tag: { connect: { id: tag.id } },
  }));

  if (isCreate) {
    return { create: createInput };
  }

  const deleteInput =
    cachedTags?.map(({ tag }) => ({
      tag: { id: tag.id },
    })) ?? [];

  return {
    delete: deleteInput,
    create: createInput,
  };
};

const getTabQualifiersMutationInput = (
  qualifiers: CategoryFormSectionBoardQualifier[],
  cachedQualifiers: TabQualifiersCache | undefined,
  isCreate: boolean,
): TabQualifiersMutationInput => {
  const createInput = qualifiers.map((qualifier, index) => ({
    order: index + 1,
    qualifier: { connect: { id: qualifier.id } },
  }));

  if (isCreate) {
    return { create: createInput };
  }

  const deleteInput =
    cachedQualifiers?.map(({ qualifier }) => ({
      qualifier: { id: qualifier.id },
    })) ?? [];

  return {
    delete: deleteInput,
    create: createInput,
  };
};

const getBoardTabRelationsMutationInput = (
  tab: {
    order: number;
    type: TabType;
    tags?: CategoryFormSectionBoardTag[] | SectionTabTag[];
    qualifiers?: CategoryFormSectionBoardQualifier[] | SectionTabQualifier[];
  },
  cachedTab:
    | {
        tags?: TabTagsCache;
        qualifiers?: TabQualifiersCache;
      }
    | undefined,
  isCreate: boolean,
) => {
  if (tab.type !== TabType.Board) {
    return {};
  }

  const tags = normalizeTabTags(tab.tags);
  const qualifiers = normalizeTabQualifiers(tab.qualifiers);

  return {
    tags: getTabTagsMutationInput(tags, cachedTab?.tags, isCreate),
    qualifiers: getTabQualifiersMutationInput(
      qualifiers,
      cachedTab?.qualifiers,
      isCreate,
    ),
  };
};

export const getTabsMutationVariables = (
  variables: UseSavePageCategoryVariables & {
    data: {
      hasDragAndDropOccurred?: boolean;
    };
  },
  client: ApolloClient<any>,
): CategoryUpdateTabsInput => {
  const {
    data: { tabs = [], hasDragAndDropOccurred = false },
    where,
  } = variables;

  // Get tabs from the cache
  const tabsCache = client.readFragment({
    id: `Category:${where.id || ''}`,
    fragment: PAGE_CATEGORY_FRAGMENT,
  })?.tabs;

  const parseAndFilterContent = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        const filtered = parsed.filter(Boolean);
        return filtered.length ? filtered : null;
      }
      // If it's an object and not null/undefined, return it
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const createTabs = tabs.map((tab) => {
    const content = parseAndFilterContent(tab.content);
    return {
      title: tab.title,
      type: tab.type,
      isOnline: tab.isOnline,
      description: tab.description?.trim() || null,
      metas: tab.metas?.trim() || null,
      ...(content && { content }),
      ...getBoardTabRelationsMutationInput(tab, undefined, true),
    };
  }) as TabCreationWithoutCategoryInput[];

  // If it's a new category or drag and drop occurred, use deleteAll
  if (hasDragAndDropOccurred) {
    return {
      deleteAll: true,
      create: createTabs,
    };
  }

  // For existing categories without drag and drop, use update/create/delete
  // Check if the tab has been saved to the database by looking for createdAt
  const newTabs = tabs.filter((tab) => !tab.createdAt);
  const existingTabs = tabs.filter((tab) => tab.createdAt);

  // Get the IDs of existing tabs in the form
  const existingTabIds = existingTabs.map((tab) => tab.id);

  // Get the IDs of all tabs from the cache
  const allTabIds = tabsCache
    ? tabsCache.map((tab: { id: string }) => tab.id)
    : [];

  // Find tabs that exist in the database but not in the form (deleted tabs)
  const deletedTabIds = allTabIds.filter(
    (id: string) => !existingTabIds.includes(id),
  );

  return {
    ...(deletedTabIds.length > 0 && {
      deleteMany: {
        id_in: deletedTabIds,
      },
    }),
    ...(newTabs.length > 0 && {
      create: newTabs.map((tab) => ({
        title: tab.title,
        type: tab.type,
        isOnline: tab.isOnline,
        description: tab.description?.trim() || null,
        metas: tab.metas?.trim() || null,
        content: parseAndFilterContent(tab.content),
        ...getBoardTabRelationsMutationInput(tab, undefined, true),
      })),
    }),
    ...(existingTabs.length > 0 && {
      update: existingTabs.map((tab) => {
        const cachedTab = tabsCache?.find(
          (cached: { order: number }) => cached.order === tab.order,
        );

        return {
          where: { order: tab.order },
          data: {
            title: tab.title,
            isOnline: tab.isOnline,
            description: tab.description?.trim() || null,
            metas: tab.metas?.trim() || null,
            content: parseAndFilterContent(tab.content),
            order: tab.order,
            ...getBoardTabRelationsMutationInput(tab, cachedTab, false),
          },
        };
      }) as CategoryUpdateTabsUpdateInput[],
    }),
  };
};

export const getTagsMutationVariables = (
  variables: UseSavePageCategoryVariables,
  isUpdate: boolean,
  client: ApolloClient<any>,
): CategoryUpdateTagsInput | CategoryCreationTagsInput => {
  const {
    data: { tags = [] },
    where,
  } = variables;

  // If there are no tags and it's not an update, return empty create
  if (tags.length === 0 && !isUpdate) {
    return {
      create: [],
    };
  }

  const tagsCache = client.readFragment({
    id: `Category:${where.id || ''}`,
    fragment: PAGE_CATEGORY_FRAGMENT,
  })?.tags;

  // For new categories, just create the new tags
  if (!isUpdate) {
    return {
      create: tags.map((tag, index) => ({
        order: index + 1,
        tag: {
          connect: { id: tag.id as string, title: tag.title as string },
        },
      })),
    };
  }

  // For existing categories, always delete all tags by order and create new ones if needed
  const deleteAllTags = tagsCache
    ? tagsCache.map(({ tag }: { tag: { id: string } }) => ({
        tag: { id: tag.id },
      }))
    : [];

  return {
    delete: deleteAllTags,
    create: tags.length
      ? tags.map((tag, index) => ({
          order: index + 1,
          tag: {
            connect: { id: tag.id as string, title: tag.title as string },
          },
        }))
      : [],
  };
};
